import { useState } from "react";
import { Loader2, Plus, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";

const LISTS = [["goals", "My goals", "e.g. Get better at fractions"], ["interests", "Things I love", "e.g. Drawing, soccer, space"], ["notes", "What my buddy remembers", "e.g. I get nervous before tests"]];

const ListEditor = ({ k, label, hint, items, onChange }) => {
  const [v, setV] = useState("");
  const add = (e) => { e.preventDefault(); if (v.trim()) { onChange([...items, v.trim()]); setV(""); } };
  return (
    <div className="rounded-3xl bg-white/70 border border-[#E6DEC3] p-6" data-testid={`memory-${k}`}>
      <p className="text-xs uppercase tracking-widest text-stone-500">{label}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {items.map((x, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1EADB] text-sm text-[#2C4A3E]" data-testid={`memory-${k}-item-${i}`}>
            {x}<button onClick={() => onChange(items.filter((_, j) => j !== i))} aria-label="Remove" data-testid={`memory-${k}-remove-${i}`}><X className="w-3 h-3" /></button>
          </span>
        ))}
        {!items.length && <span className="text-sm text-stone-400">Nothing yet</span>}
      </div>
      <form onSubmit={add} className="flex gap-2 mt-4">
        <input value={v} onChange={(e) => setV(e.target.value)} maxLength={200} placeholder={hint} data-testid={`memory-${k}-input`}
          className="flex-1 px-4 py-2 rounded-full bg-white border border-[#E6DEC3] focus:outline-none focus:border-[#2C4A3E] text-sm" />
        <button data-testid={`memory-${k}-add-btn`} className="w-9 h-9 rounded-full bg-[#2C4A3E] text-white grid place-items-center"><Plus className="w-4 h-4" /></button>
      </form>
    </div>
  );
};

export const BuddyMemory = () => {
  const { buddy, setBuddy } = useProfile();
  const [busy, setBusy] = useState(false);
  const update = async (k, items) => {
    try { setBuddy((await http.put("/buddy/memory", { ...buddy.memory, [k]: items })).data.buddy); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const refresh = async () => {
    setBusy(true);
    try { setBuddy((await http.post("/buddy/memory/refresh")).data.buddy); toast.success(`${buddy.name} updated their memory`); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div data-testid="buddy-memory">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <p className="text-stone-600 max-w-xl text-sm">{buddy.name} remembers these and brings them up while you learn, along with your recent reflections and open assignments. You're always in control.</p>
        <button onClick={refresh} disabled={busy} data-testid="memory-refresh-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E6DEC3] text-sm text-[#2C4A3E] hover:bg-[#F1EADB] disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Learn from our chats
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-5">
        {LISTS.map(([k, label, hint]) => <ListEditor key={k} k={k} label={label} hint={hint} items={buddy.memory[k] || []} onChange={(items) => update(k, items)} />)}
      </div>
    </div>
  );
};
